import { useTriagem } from '../lib/triagem';
import { useEffect, useRef, useState } from 'react';
import {
  Archive,
  ArrowLeft,
  Clock,
  EllipsisVertical,
  Flag,
  Forward,
  Mail,
  RefreshCw,
  Reply,
  ReplyAll,
  Download,
  Paperclip,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import {
  Alert,
  Avatar,
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownSeparator,
  DropdownTrigger,
  EmptyState,
  Tooltip,
  useToast,
} from '@waymail/ui';
import { baixarArquivo, ErroDaApi, ORIGEM_DA_API } from '../lib/api';
import { rotuloDoTipo } from '../lib/sinaisDeAutenticacao';
import { EnderecoEAvisosDoRemetente, SeloDeAutenticacao } from './SinaisDeAutenticacao';
import { nomeNoAvatar } from '../lib/avatar';
import { fileSize, fullDate, listTime } from '../lib/date';
import {
  descreverAdiamento,
  horarioDeAdiamento,
  OPCOES_DE_ADIAMENTO,
  type QuandoAdiar,
} from '../lib/adiar';
import { textoDoLembrete, useMarcarLembrete } from '../lib/lembretes';
import {
  useAcaoEmMassa,
  useAdiar,
  useContas,
  useCorpo,
  useMensagem,
  usePastas,
  usePreferencias,
  useSalvarPreferencias,
  useSalvarRegra,
  type AcaoEmMassa,
} from '../lib/consultas';
import { regraDeBloqueio } from '../lib/regras';
import { remetenteConfiavel } from '../lib/remetentes';
import { rotularPastas } from '../lib/pastas';
import { rascunhoDeResposta, type TipoDeResposta } from '../lib/resposta';
import { useShortcuts } from '../lib/shortcuts';
import { apontarImagensParaApi, contratoDeRenderizacao } from '../lib/renderizacao';
import { useUi } from '../store/ui';

/**
 * As tres acoes de triagem da barra, todas ligadas.
 *
 * O comentario anterior aqui dizia que `action: null` era botao sem destino e
 * que adiar dependia de um agendador inexistente. As duas coisas deixaram de
 * valer: nenhuma entrada tem `action: null`, e adiar funciona sem agendador
 * nenhum - a lista filtra por `snoozed_until is null or snoozed_until <= now()`,
 * entao a mensagem volta sozinha quando a hora chega. Comentario que descreve
 * um estado passado faz quem le desconfiar de recurso que funciona.
 */
const ACOES: {
  icon: typeof Archive;
  label: string;
  action: AcaoEmMassa;
  feito: string;
}[] = [
  { icon: Archive, label: 'Arquivar', action: 'archive', feito: 'Arquivada.' },
  { icon: Trash2, label: 'Excluir', action: 'delete', feito: 'Movida para a lixeira.' },
  { icon: Flag, label: 'Sinalizar', action: 'flag', feito: 'Sinalizada.' },
];

const RESPOSTAS: { icon: typeof Reply; label: string; tipo: TipoDeResposta }[] = [
  { icon: Reply, label: 'Responder', tipo: 'responder' },
  { icon: ReplyAll, label: 'Responder a todos', tipo: 'responder_a_todos' },
  { icon: Forward, label: 'Encaminhar', tipo: 'encaminhar' },
];

/**
 * Tempo com a mensagem aberta ate ela contar como lida, quando a preferencia
 * do usuario nao pode ser lida. Igual ao default do servidor.
 */
const MARCAR_LIDA_PADRAO_MS = 1000;

export function ReadingPane({
  messageId,
  onBack,
}: {
  messageId: string | null;
  onBack?: () => void;
}) {
  const [imagensLiberadas, setImagensLiberadas] = useState(false);
  const mensagem = useMensagem(messageId);
  const preferencias = usePreferencias();
  const salvarPreferencias = useSalvarPreferencias();
  // Preferencia que nao carregou nao trava a leitura: vale o default.
  const leitura = preferencias.isError ? undefined : preferencias.data?.leitura;
  const confiaveis = preferencias.isError ? [] : leitura?.imagensSempreDe;
  const remetente = mensagem.data?.from?.address;
  const confiavel = remetenteConfiavel(remetente, confiaveis ?? []);

  /*
   * Remetente da lista "sempre exibir imagens" (ADR 0045) pede o corpo ja com
   * imagens. Para saber, e preciso a lista e o remetente; sem esperar, o corpo
   * viria bloqueado e trocaria logo depois, piscando. Com a lista vazia - o
   * caso comum - nao ha o que esperar, e o corpo sai junto com o cabecalho.
   */
  const decidido =
    confiaveis !== undefined && (confiaveis.length === 0 || mensagem.data !== undefined);
  const corpo = useCorpo(decidido ? messageId : null, imagensLiberadas || confiavel);
  const acao = useAcaoEmMassa();
  const triagem = useTriagem();
  const adiar = useAdiar();
  const marcarLembrete = useMarcarLembrete();
  const contas = useContas();
  const selectMensagem = useUi((s) => s.selectMensagem);
  const pedirTarefa = useUi((s) => s.pedirTarefa);
  const abrirComposerCom = useUi((s) => s.abrirComposerCom);
  const pastas = usePastas(mensagem.data?.accountId);

  /*
   * Abrir marca como lida, como no Outlook. Espera um instante para nao marcar
   * tudo por onde o J/K passou, e so uma vez por mensagem: depois de "Marcar
   * como nao lida" com ela aberta, a escolha da pessoa vale.
   */
  const marcadaAoAbrir = useRef<string | null>(null);
  const naoLida = mensagem.data !== undefined && !mensagem.data.flags.includes('\\Seen');
  const { mutate: agir } = acao;
  // `undefined` e "ainda nao sei": quem escolheu "Nunca" nao pode ver a
  // mensagem marcada pelo default no segundo antes de a preferencia chegar.
  const marcarApos = preferencias.isError
    ? MARCAR_LIDA_PADRAO_MS
    : leitura === undefined
      ? undefined
      : leitura.marcarLidaAposMs;
  useEffect(() => {
    if (messageId === null || !naoLida || marcadaAoAbrir.current === messageId) return;
    if (marcarApos === undefined || marcarApos === null) return;
    const t = window.setTimeout(() => {
      marcadaAoAbrir.current = messageId;
      agir({ ids: [messageId], action: 'read' });
    }, marcarApos);
    return () => window.clearTimeout(t);
  }, [messageId, naoLida, agir, marcarApos]);
  const salvarRegra = useSalvarRegra();

  // Nao oferece a pasta onde a mensagem ja esta, nem as de papel que ja tem
  // botao proprio - `Arquivar`, `Excluir` e `Marcar spam` fazem o mesmo com um
  // clique a menos.
  const destinos = rotularPastas(
    (pastas.data ?? []).filter((p) => p.role === null || p.role === 'inbox'),
  );
  const toast = useToast();

  // Resposta pedida antes de a mensagem e o corpo chegarem (ver `responder`).
  const respostaPendente = useRef<TipoDeResposta | null>(null);

  /**
   * Abre o composer ja preenchido. A conta que recebeu a mensagem assina a
   * resposta - responder pela caixa errada e o jeito mais rapido de a resposta
   * cair no spam de quem recebe.
   */
  function responder(tipo: TipoDeResposta) {
    const m = mensagem.data;
    // Mensagem ou corpo ainda a caminho: guarda o pedido e responde quando chegar.
    // Antes a tecla nao fazia nada em silencio, e com a mensagem ja aberta mas o
    // corpo atrasado a resposta saia sem o texto citado.
    if (m === undefined || (corpo.data === undefined && !corpo.isError)) {
      respostaPendente.current = tipo;
      return;
    }

    const minha = contas.data?.find((c) => c.id === m.accountId) ?? contas.data?.[0];
    abrirComposerCom(
      rascunhoDeResposta({
        tipo,
        mensagem: m,
        corpoHtml: corpo.data?.html ?? '',
        meusEnderecos: contas.data?.map((c) => c.email) ?? [],
        identidade: minha?.email ?? '',
        responderAcima: preferencias.data?.redacao.responderAcima ?? true,
      }),
    );
  }

  /**
   * As tres teclas da Secao 6.8 que a tela de atalhos anunciava e ninguem tinha
   * ligado: `r`, `a` e `f` nao faziam nada, e o criterio 7 do MVP exige
   * responder so com teclado. Ficam aqui, e nao no AppShell, porque e este
   * componente que tem a mensagem e o corpo carregados - o shell so tem o id.
   */
  useEffect(() => {
    respostaPendente.current = null; // outra mensagem: o pedido era da anterior
  }, [messageId]);
  useEffect(() => {
    const tipo = respostaPendente.current;
    if (tipo === null) return;
    if (mensagem.data === undefined || (corpo.data === undefined && !corpo.isError)) return;
    respostaPendente.current = null;
    responder(tipo);
  });

  useShortcuts({
    reply: () => responder('responder'),
    replyAll: () => responder('responder_a_todos'),
    forward: () => responder('encaminhar'),
  });

  function acompanharPara(quando: QuandoAdiar) {
    if (messageId === null) return;
    marcarLembrete.mutate(
      { messageId, quando },
      {
        onSuccess: () => toast({ title: textoDoLembrete(quando) }),
        onError: (e) => toast({ title: e.message, tone: 'error' }),
      },
    );
  }

  /**
   * Adiar esconde a mensagem da lista ate a hora marcada. Nao ha nada agendado:
   * a comparacao acontece na consulta, entao ela volta sozinha.
   */
  function adiarPara(quando: QuandoAdiar) {
    if (messageId === null) return;
    const alvo = horarioDeAdiamento(quando);

    adiar.mutate(
      { messageId, until: alvo.toISOString() },
      {
        onSuccess: () => {
          selectMensagem(null);
          toast({ title: `Volta em ${descreverAdiamento(alvo)}.` });
        },
        onError: (e) =>
          toast({
            title: e instanceof Error ? e.message : 'Não foi possível adiar.',
            tone: 'error',
          }),
      },
    );
  }

  /**
   * Imprime o corpo, nao a aplicacao inteira: manda o comando para o iframe
   * isolado, que so contem o HTML sanitizado da mensagem. Imprimir a janela
   * sairia com barra lateral, lista e cabecalho no papel.
   */
  function imprimir() {
    const quadro = document.querySelector<HTMLIFrameElement>('iframe[title="Corpo da mensagem"]');
    const janela = quadro?.contentWindow;

    if (janela === null || janela === undefined) {
      toast({ title: 'Não foi possível abrir a impressao.', tone: 'error' });
      return;
    }

    janela.focus();
    janela.print();
  }

  /**
   * O arquivo nao esta guardado no servidor: e buscado no IMAP no momento do
   * clique. Extensao executavel exige uma confirmacao a mais (Secao 15.6) - a
   * API recusa sem ela, e recusar por padrao e o que impede um clique distraido
   * de entregar um `.exe`.
   */
  async function baixar(anexo: { id: string; filename: string; bloqueado: boolean }) {
    if (messageId === null) return;

    if (anexo.bloqueado) {
      const segue = window.confirm(
        `"${anexo.filename}" tem extensão de programa executável. Arquivos assim são o jeito mais comum de instalar vírus. Baixar mesmo assim?`,
      );
      if (!segue) return;
    }

    const caminho = `/messages/${messageId}/attachments/${anexo.id}`;
    try {
      try {
        await baixarArquivo(anexo.bloqueado ? `${caminho}?perigoso=sim` : caminho, anexo.filename);
      } catch (e) {
        // Os bytes revelaram um programa que o nome escondia: so segue com confirmacao.
        if (!(e instanceof ErroDaApi) || e.status !== 403 || anexo.bloqueado) throw e;
        const segue = window.confirm(
          `${e.message}\n\n"${anexo.filename}" parece um documento, mas o conteúdo é de um programa. Baixar mesmo assim?`,
        );
        if (!segue) return;
        await baixarArquivo(`${caminho}?perigoso=sim`, anexo.filename);
      }
    } catch (e) {
      toast({
        title: e instanceof Error ? e.message : 'Não foi possível baixar o anexo.',
        tone: 'error',
      });
    }
  }

  /**
   * A mudanca vale na hora na tela; o comando IMAP fica na fila e o supervisor
   * o entrega (Secao 10.4). Arquivar e excluir tiram a mensagem da lista, entao
   * a leitura precisa soltar a selecao junto.
   */
  function executar(action: AcaoEmMassa, rotulo: string, folderId?: string) {
    if (messageId === null) return;
    const saiDaLista =
      action === 'archive' || action === 'delete' || action === 'spam' || action === 'move';

    // Sai da pasta: passa por `triar`, que oferece Desfazer (ADR 0054).
    if (saiDaLista) {
      void triagem.executar([messageId], action, {
        ...(folderId !== undefined ? { destino: folderId } : {}),
        titulo: rotulo,
        aoConcluir: () => selectMensagem(null),
      });
      return;
    }

    acao.mutate(
      { ids: [messageId], action, ...(folderId !== undefined ? { folderId } : {}) },
      {
        onSuccess: () => {
          if (saiDaLista) selectMensagem(null);
          toast({ title: rotulo });
        },
        onError: (e) => {
          toast({
            title: e instanceof Error ? e.message : 'Não foi possível concluir a ação.',
            tone: 'error',
          });
        },
      },
    );
  }

  /**
   * Bloquear e criar uma regra (ADR 0042) e tirar esta mensagem da Entrada.
   * A regra pega o que chegar depois; a que esta aberta vai para a lixeira
   * pelo mesmo caminho do botao Excluir. Regra primeiro: se ela falhar, a
   * mensagem fica onde esta e a pessoa ve o erro, em vez de achar que bloqueou.
   */
  function bloquearRemetente() {
    const m = mensagem.data;
    const endereco = m?.from?.address;
    if (m === undefined || endereco === undefined) return;
    salvarRegra.mutate(
      { id: null, dados: regraDeBloqueio(m.accountId, endereco) },
      {
        onSuccess: () =>
          executar(
            'delete',
            'Remetente bloqueado. As próximas mensagens vão direto para a lixeira.',
          ),
        onError: (e) => toast({ title: e.message, tone: 'error' }),
      },
    );
  }

  if (messageId === null) {
    return (
      <section
        aria-label="Leitura"
        className="flex w-full min-w-0 flex-1 items-center justify-center bg-app"
      >
        <EmptyState
          icon={Mail}
          title="Selecione uma conversa."
          description="Use J e K para percorrer a lista sem tocar no mouse."
        />
      </section>
    );
  }

  if (mensagem.isLoading) {
    return (
      <section
        aria-label="Leitura"
        className="flex w-full min-w-0 flex-1 items-center justify-center bg-app"
      >
        <RefreshCw
          size={20}
          aria-label="Carregando mensagem"
          className="animate-spin text-tertiary motion-reduce:animate-none"
        />
      </section>
    );
  }

  if (mensagem.isError || mensagem.data === undefined) {
    return (
      <section
        aria-label="Leitura"
        className="flex w-full min-w-0 flex-1 items-center justify-center bg-app"
      >
        <Alert tone="error" className="max-w-md">
          Não foi possível carregar esta mensagem. Tente sincronizar a conta de novo.
        </Alert>
      </section>
    );
  }

  const m = mensagem.data;

  return (
    <section aria-label="Leitura" className="flex w-full min-w-0 flex-1 flex-col bg-app">
      <header className="flex flex-wrap items-start gap-x-3 gap-y-1 py-4 pt-7 pr-4 pl-8 md:pr-20">
        {onBack !== undefined && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Voltar à lista"
            className="mt-1 flex size-8 toque:size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg text-secondary hover:bg-hover"
          >
            <ArrowLeft size={18} strokeWidth={1.8} aria-hidden="true" />
          </button>
        )}

        <h1 className="min-w-40 flex-1 truncate text-[26px] leading-[1.2] font-bold tracking-[-0.02em] text-primary">
          {m.subject ?? '(sem assunto)'}
        </h1>

        <div className="flex shrink-0 items-center gap-0.5">
          {ACOES.map(({ icon: Icon, label, action, feito }) => (
            <Tooltip key={label} content={label}>
              <button
                type="button"
                aria-label={label}
                disabled={acao.isPending}
                onClick={() => executar(action, feito)}
                className="flex size-8 cursor-pointer items-center justify-center rounded-lg text-secondary toque:size-10 transition-colors hover:bg-hover hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
              </button>
            </Tooltip>
          ))}
          <Dropdown>
            <DropdownTrigger asChild>
              <button
                type="button"
                aria-label="Adiar"
                disabled={adiar.isPending}
                className="flex size-8 cursor-pointer items-center justify-center rounded-lg text-secondary toque:size-10 transition-colors hover:bg-hover hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Clock size={16} strokeWidth={1.8} aria-hidden="true" />
              </button>
            </DropdownTrigger>
            <DropdownContent align="end">
              <DropdownLabel>Voltar para a Entrada</DropdownLabel>
              {OPCOES_DE_ADIAMENTO.map((o) => (
                <DropdownItem key={o.id} onSelect={() => adiarPara(o.id)}>
                  {o.rotulo}
                </DropdownItem>
              ))}
              <DropdownSeparator />
              <DropdownLabel>Acompanhar se não houver resposta</DropdownLabel>
              {OPCOES_DE_ADIAMENTO.map((o) => (
                <DropdownItem key={`lembrete-${o.id}`} onSelect={() => acompanharPara(o.id)}>
                  {o.rotulo}
                </DropdownItem>
              ))}
            </DropdownContent>
          </Dropdown>

          <Dropdown>
            <DropdownTrigger asChild>
              <button
                type="button"
                aria-label="Mais ações"
                className="flex size-8 cursor-pointer items-center justify-center rounded-lg text-secondary toque:size-10 transition-colors hover:bg-hover hover:text-primary"
              >
                <EllipsisVertical size={16} strokeWidth={1.8} aria-hidden="true" />
              </button>
            </DropdownTrigger>
            <DropdownContent align="end">
              <DropdownItem onSelect={() => executar('unread', 'Marcada como não lida.')}>
                Marcar como não lida
              </DropdownItem>
              <DropdownItem
                onSelect={() =>
                  pedirTarefa({ tipo: 'criar', messageId: m.id, assunto: m.subject ?? '' })
                }
              >
                Criar tarefa
              </DropdownItem>
              <DropdownItem onSelect={imprimir}>Imprimir</DropdownItem>
              <DropdownSeparator />
              <DropdownLabel>Mover para</DropdownLabel>
              {destinos.length === 0 ? (
                <DropdownItem disabled>Nenhuma outra pasta</DropdownItem>
              ) : (
                <div className="max-h-56 overflow-y-auto">
                  {destinos.map((p) => (
                    <DropdownItem
                      key={p.pasta.id}
                      onSelect={() => executar('move', `Movida para ${p.rotulo}.`, p.pasta.id)}
                    >
                      {p.rotulo}
                    </DropdownItem>
                  ))}
                </div>
              )}
              <DropdownSeparator />
              <DropdownItem danger onSelect={() => executar('spam', 'Movida para spam.')}>
                Marcar spam
              </DropdownItem>
              <DropdownItem
                danger
                disabled={m.from === null || salvarRegra.isPending}
                onSelect={bloquearRemetente}
              >
                Bloquear remetente
              </DropdownItem>
            </DropdownContent>
          </Dropdown>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-8">
        <div className="flex shrink-0 items-center gap-3">
          <Avatar size="md" name={nomeNoAvatar(m.from)} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[15px] font-bold text-primary">
                {m.from?.name || m.from?.address || '(sem remetente)'}
              </span>
              <SeloDeAutenticacao auth={m.authResults ?? null} />
            </div>
            <p className="text-[13px] text-tertiary" title={fullDate(m.sentAt)}>
              Para: {m.to[0]?.name || m.to[0]?.address || 'mim'}
            </p>
          </div>
          <span className="shrink-0 text-[12px] text-tertiary">{listTime(m.sentAt)}</span>
        </div>

        <EnderecoEAvisosDoRemetente de={m.from} replyTo={m.replyTo} />

        {corpo.data !== undefined && corpo.data.imagensRemotasBloqueadas > 0 && (
          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-lg bg-sunken px-3 py-2">
            <p className="min-w-0 flex-1 text-[12px] text-secondary">
              {corpo.data.imagensRemotasBloqueadas} imagem(ns) remota(s) bloqueada(s) para proteger
              sua privacidade.
            </p>
            <button
              type="button"
              onClick={() => setImagensLiberadas(true)}
              className="shrink-0 cursor-pointer text-[12px] font-semibold text-accent hover:underline"
            >
              Exibir uma vez
            </button>
            {remetente !== undefined && confiaveis !== undefined && (
              <button
                type="button"
                disabled={salvarPreferencias.isPending}
                // O endereco, nao o dominio: confiar no dominio inteiro e
                // decisao maior, e fica para a aba Leitura das Configuracoes.
                onClick={() =>
                  salvarPreferencias.mutate(
                    {
                      leitura: {
                        imagensSempreDe: [...confiaveis, remetente.trim().toLowerCase()],
                      },
                    },
                    {
                      onSuccess: () =>
                        toast({ title: `Imagens de ${remetente} sempre serão exibidas.` }),
                      onError: (e) => toast({ title: e.message, tone: 'error' }),
                    },
                  )
                }
                className="shrink-0 cursor-pointer text-[12px] font-semibold text-accent hover:underline"
              >
                Sempre exibir deste remetente
              </button>
            )}
          </div>
        )}

        {corpo.data !== undefined && corpo.data.linksSuspeitos.length > 0 && (
          <Alert tone="warning" className="mt-3">
            Esta mensagem tem link para domínio parecido com marca conhecida. Confira o endereço
            antes de clicar.
          </Alert>
        )}

        {m.attachments.length > 0 && (
          <ul className="mt-5 flex flex-wrap gap-2">
            {m.attachments.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => void baixar(a)}
                  className="flex h-16 w-[220px] cursor-pointer items-center gap-2.5 rounded-lg border border-subtle px-3 text-left transition-colors hover:bg-hover"
                >
                  {a.bloqueado ? (
                    <ShieldAlert
                      size={18}
                      strokeWidth={1.8}
                      aria-hidden="true"
                      className="shrink-0 text-error"
                    />
                  ) : (
                    <Paperclip
                      size={18}
                      strokeWidth={1.8}
                      aria-hidden="true"
                      className="shrink-0 text-tertiary"
                    />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-primary">
                      {a.filename}
                    </span>
                    <span className="block text-[12px] text-tertiary">
                      {a.bloqueado
                        ? 'Executável · confirme antes'
                        : `${rotuloDoTipo(a.contentType, a.filename)} · ${fileSize(a.sizeBytes)}`}
                    </span>
                  </span>
                  <Download
                    size={16}
                    strokeWidth={1.8}
                    aria-hidden="true"
                    className="shrink-0 text-tertiary"
                  />
                </button>
              </li>
            ))}
          </ul>
        )}

        {corpo.isError && (
          <Alert tone="error" className="mt-5">
            {corpo.error instanceof ErroDaApi
              ? corpo.error.message
              : 'Não foi possível carregar o conteúdo da mensagem. Verifique a conexão.'}{' '}
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              loading={corpo.isFetching}
              onClick={() => void corpo.refetch()}
            >
              Tentar de novo
            </Button>
          </Alert>
        )}

        <CorpoIsolado html={corpo.data?.html ?? ''} carregando={corpo.isLoading || !decidido} />
      </div>

      <footer className="flex shrink-0 flex-wrap items-center gap-2 px-8 py-5">
        {RESPOSTAS.map(({ icon: Icon, label, tipo }) => (
          <button
            key={label}
            type="button"
            onClick={() => responder(tipo)}
            className="flex h-9 toque:h-10 cursor-pointer items-center gap-2 rounded-lg border border-strong px-4 text-[13px] font-semibold text-primary transition-colors hover:bg-hover"
          >
            <Icon size={15} strokeWidth={1.8} aria-hidden="true" />
            {label}
          </button>
        ))}
      </footer>
    </section>
  );
}

/**
 * Camada 3 da Secao 15.5: o corpo vive dentro de um iframe com sandbox, sem
 * `allow-scripts` e sem `allow-same-origin`. Sem `allow-same-origin` o iframe
 * fica em origem opaca - nao alcanca cookie, storage nem DOM da aplicacao.
 *
 * ponytail: `srcDoc` em vez de `content.waymail.app`. Para isolamento de
 * script, origem opaca e equivalente; o dominio separado importa quando houver
 * cookie no jogo, e entra junto com o deploy (P10). A altura nao segue o
 * conteudo, e o corpo rola por dentro, porque ajustar por `postMessage` exigiria
 * script dentro do iframe - exatamente o que o sandbox proibe. Ela vem do
 * `flex-1`: ocupa o que sobra ate os botoes de resposta. A conta fixa que havia
 * antes (`100vh - 380px`) deixava uma faixa vazia grande em tela alta.
 */
function CorpoIsolado({ html, carregando }: { html: string; carregando: boolean }) {
  if (carregando) {
    return (
      <div className="mt-6 flex h-40 items-center justify-center">
        <RefreshCw
          size={18}
          aria-label="Carregando corpo"
          className="animate-spin text-tertiary motion-reduce:animate-none"
        />
      </div>
    );
  }

  const documento = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<meta name="referrer" content="no-referrer">
<style>
  html,body{margin:0;padding:0;background:transparent;
    font:14px/1.7 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:#2e3b5e}
  img{max-width:100%;height:auto}
  a{color:#1d66ff}
  table{max-width:100%}
</style></head><body>${apontarImagensParaApi(html, ORIGEM_DA_API)}</body></html>`;

  return (
    <iframe
      title="Corpo da mensagem"
      sandbox={contratoDeRenderizacao.sandbox}
      referrerPolicy="no-referrer"
      srcDoc={documento}
      className="mt-6 min-h-80 w-full flex-1 border-0"
    />
  );
}
